/**
 * User Settings View
 * Personal settings for regular users (profile, preferences, notifications)
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Bell,
  Shield,
  Palette,
  Keyboard,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Save,
  Check,
  AlertCircle,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

interface SettingSection {
  id: string;
  title: string;
  icon: any;
  color: string;
}

const SECTIONS: SettingSection[] = [
  { id: "profile", title: "Profile", icon: User, color: "text-blue-500" },
  { id: "notifications", title: "Notifications", icon: Bell, color: "text-purple-500" },
  { id: "security", title: "Security", icon: Shield, color: "text-green-500" },
  { id: "appearance", title: "Appearance", icon: Palette, color: "text-pink-500" },
  { id: "accessibility", title: "Accessibility", icon: Keyboard, color: "text-orange-500" },
  { id: "region", title: "Region & Language", icon: Globe, color: "text-cyan-500" },
];

export function UserSettingsView() {
  const { addLogEntry } = useDashboardStore();
  const [activeSection, setActiveSection] = useState("profile");
  const [showPassword, setShowPassword] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    company: "",
  });

  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    pushNotifications: true,
    weeklyDigest: false,
    securityAlerts: true,
    usageReports: false,
  });

  const [appearance, setAppearance] = useState({
    theme: "dark" as "light" | "dark" | "system",
    accentColor: "blue",
    animations: true,
    compactMode: false,
  });

  const [security, setSecurity] = useState({
    twoFactor: false,
    loginNotifications: true,
    sessionTimeout: 24,
  });

  const ACCENT_COLORS = [
    { id: "blue", color: "bg-blue-500" },
    { id: "purple", color: "bg-purple-500" },
    { id: "green", color: "bg-green-500" },
    { id: "orange", color: "bg-orange-500" },
    { id: "pink", color: "bg-pink-500" },
    { id: "cyan", color: "bg-cyan-500" },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    setProfile({
      name: "John Doe",
      email: "john@example.com",
      company: "Acme Corp",
    });
  };

  const saveSettings = async () => {
    setSaveStatus("saving");
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    addLogEntry({
      category: "user",
      action: "Settings Updated",
      details: `Updated ${activeSection} settings`,
      severity: "info",
      source: "user-settings",
    });

    setSaveStatus("saved");
    setTimeout(() => setSaveStatus("idle"), 2000);
  };

  const renderProfileSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Profile Information</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Full Name</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email Address</label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Company (Optional)</label>
            <input
              type="text"
              value={profile.company}
              onChange={(e) => setProfile({ ...profile, company: e.target.value })}
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all"
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary)/.6)] text-2xl font-bold text-[hsl(var(--primary-foreground))]">
            {profile.name.split(" ").map(n => n[0]).join("")}
          </div>
          <div>
            <p className="font-semibold">{profile.name}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Passkey Authenticated</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
      <div className="space-y-4">
        {[
          { key: "emailAlerts", label: "Email Alerts", desc: "Receive notifications via email" },
          { key: "pushNotifications", label: "Push Notifications", desc: "Browser push notifications" },
          { key: "weeklyDigest", label: "Weekly Digest", desc: "Summary of weekly activity" },
          { key: "securityAlerts", label: "Security Alerts", desc: "Important security notifications" },
          { key: "usageReports", label: "Usage Reports", desc: "Monthly usage and billing reports" },
        ].map((item) => (
          <div
            key={item.key}
            className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4"
          >
            <div>
              <p className="font-medium">{item.label}</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{item.desc}</p>
            </div>
            <button
              onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key as keyof typeof notifications] })}
              className={cn(
                "relative h-6 w-11 rounded-full transition-colors",
                notifications[item.key as keyof typeof notifications]
                  ? "bg-[hsl(var(--primary))]"
                  : "bg-[hsl(var(--muted))]"
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform",
                  notifications[item.key as keyof typeof notifications] ? "translate-x-6" : "translate-x-0.5"
                )}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Security Settings</h3>
      
      <div className="space-y-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-semibold">Two-Factor Authentication</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Add an extra layer of security</p>
            </div>
            <button
              onClick={() => setSecurity({ ...security, twoFactor: !security.twoFactor })}
              className={cn(
                "relative h-6 w-11 rounded-full transition-colors",
                security.twoFactor ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]"
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform",
                  security.twoFactor ? "translate-x-6" : "translate-x-0.5"
                )}
              />
            </button>
          </div>
          {!security.twoFactor && (
            <button className="text-sm text-[hsl(var(--primary))] hover:underline cursor-pointer">
              Enable 2FA
            </button>
          )}
        </div>

        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <Lock className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="font-semibold">Passkey Authentication</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Active and secure</p>
            </div>
          </div>
          <button className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors cursor-pointer">
            Manage Passkeys
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Session Timeout</label>
          <select
            value={security.sessionTimeout}
            onChange={(e) => setSecurity({ ...security, sessionTimeout: Number(e.target.value) })}
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] transition-all"
          >
            <option value={1}>1 hour</option>
            <option value={8}>8 hours</option>
            <option value={24}>24 hours</option>
            <option value={168}>1 week</option>
            <option value={720}>1 month</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Appearance</h3>
      
      <div>
        <label className="block text-sm font-medium mb-3">Theme</label>
        <div className="grid grid-cols-3 gap-3">
          {(["light", "dark", "system"] as const).map((theme) => (
            <button
              key={theme}
              onClick={() => setAppearance({ ...appearance, theme })}
              className={cn(
                "flex flex-col items-center gap-2 rounded-lg border p-4 transition-all",
                appearance.theme === theme
                  ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.1)]"
                  : "border-[hsl(var(--border))] hover:border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
              )}
            >
              <div className={cn(
                "flex h-12 w-12 items-center justify-center rounded-lg",
                theme === "light" ? "bg-white border" : theme === "dark" ? "bg-gray-900" : "bg-gradient-to-br from-white to-gray-900 border"
              )}>
                {theme === "light" ? (
                  <span className="text-2xl">☀️</span>
                ) : theme === "dark" ? (
                  <span className="text-2xl">🌙</span>
                ) : (
                  <span className="text-2xl">💻</span>
                )}
              </div>
              <span className="text-sm font-medium capitalize">{theme}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-3">Accent Color</label>
        <div className="flex gap-3">
          {ACCENT_COLORS.map((color) => (
            <button
              key={color.id}
              onClick={() => setAppearance({ ...appearance, accentColor: color.id })}
              className={cn(
                "h-10 w-10 rounded-full transition-transform hover:scale-110",
                color.color,
                appearance.accentColor === color.id && "ring-2 ring-offset-2 ring-offset-background"
              )}
            />
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {[
          { key: "animations", label: "Enable Animations", desc: "Smooth transitions and animations" },
          { key: "compactMode", label: "Compact Mode", desc: "Reduce padding and spacing" },
        ].map((item) => (
          <div
            key={item.key}
            className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4"
          >
            <div>
              <p className="font-medium">{item.label}</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{item.desc}</p>
            </div>
            <button
              onClick={() => setAppearance({ ...appearance, [item.key]: !appearance[item.key as keyof typeof appearance] })}
              className={cn(
                "relative h-6 w-11 rounded-full transition-colors",
                appearance[item.key as keyof typeof appearance]
                  ? "bg-[hsl(var(--primary))]"
                  : "bg-[hsl(var(--muted))]"
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform",
                  appearance[item.key as keyof typeof appearance] ? "translate-x-6" : "translate-x-0.5"
                )}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAccessibilitySection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Accessibility</h3>
      <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-8 text-center">
        <Keyboard className="mx-auto h-12 w-12 text-[hsl(var(--muted-foreground))]" />
        <p className="mt-4 text-[hsl(var(--muted-foreground))]">Accessibility options coming soon</p>
      </div>
    </div>
  );

  const renderRegionSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Region & Language</h3>
      <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-8 text-center">
        <Globe className="mx-auto h-12 w-12 text-[hsl(var(--muted-foreground))]" />
        <p className="mt-4 text-[hsl(var(--muted-foreground))]">Region settings coming soon</p>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "profile": return renderProfileSection();
      case "notifications": return renderNotificationsSection();
      case "security": return renderSecuritySection();
      case "appearance": return renderAppearanceSection();
      case "accessibility": return renderAccessibilitySection();
      case "region": return renderRegionSection();
      default: return renderProfileSection();
    }
  };

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <motion.aside
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="w-64 shrink-0 space-y-2"
      >
        {SECTIONS.map((section, i) => (
          <motion.button
            key={section.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            onClick={() => setActiveSection(section.id)}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors cursor-pointer",
              activeSection === section.id
                ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                : "hover:bg-[hsl(var(--muted))]"
            )}
          >
            <section.icon className={cn("h-5 w-5", activeSection !== section.id && section.color)} />
            <span className="text-sm font-medium">{section.title}</span>
          </motion.button>
        ))}
      </motion.aside>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex-1 min-w-0"
      >
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          {renderContent()}
        </div>

        {/* Save Button */}
        <div className="mt-6 flex items-center justify-end gap-3">
          {saveStatus === "error" && (
            <div className="flex items-center gap-2 text-red-500">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Failed to save changes</span>
            </div>
          )}
          {saveStatus === "saved" && (
            <div className="flex items-center gap-2 text-green-500">
              <Check className="h-4 w-4" />
              <span className="text-sm">Changes saved</span>
            </div>
          )}
          <button
            onClick={saveSettings}
            disabled={saveStatus === "saving"}
            className="flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))] px-6 py-2.5 text-sm font-medium text-[hsl(var(--primary-foreground))] disabled:opacity-50 hover:bg-[hsl(var(--primary)/.9)] transition-colors cursor-pointer"
          >
            {saveStatus === "saving" ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-[hsl(var(--primary-foreground))] border-t-transparent" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
